import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  SelectChangeEvent,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/api/axios';
import { ENDPOINTS } from '@/api/endpoints';
import { useOrgStore, useCurrentOrg, useWorkspaces, useWorkspaceStore } from '@/store';

export const OrgSelector: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orgs, currentOrgId, fetchOrgs, setCurrentOrgId, addOrg } = useOrgStore();
  const currentOrg = useCurrentOrg();
  const allWorkspaces = useWorkspaces();
  const setSelectedWorkspace = useWorkspaceStore((s) => s.setSelectedWorkspace);
  const selectedWorkspace = useWorkspaceStore((s) => s.selectedWorkspace);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  // Track which orgId we already handled to prevent re-running on re-renders
  const lastHandledOrgId = useRef<string | null>(null);

  useEffect(() => {
    fetchOrgs();
  }, [fetchOrgs]);

  // When user explicitly switches org, reset workspace/project state and move to a
  // workspace that belongs to the new org (or clear it if the org has none).
  useEffect(() => {
    if (!currentOrgId) return;
    if (lastHandledOrgId.current === currentOrgId) return;

    // Already on a workspace in this org — nothing to reset. (Handled.)
    if (selectedWorkspace?.organizationId === currentOrgId) {
      lastHandledOrgId.current = currentOrgId;
      return;
    }

    // Wait until workspaces have loaded before deciding. Acting on an empty list during
    // the initial load would wrongly clear the workspace (and previously navigated to the
    // public home page, which looked like a logout). Do NOT mark as handled yet.
    if (allWorkspaces.length === 0) return;

    const orgWorkspaces = allWorkspaces.filter(w => w.organizationId === currentOrgId);
    lastHandledOrgId.current = currentOrgId;

    if (orgWorkspaces.length === 0) {
      // This org genuinely has no accessible workspaces: clear stale workspace/project
      // state so the user never sees another org's data. Stay in the app shell — the
      // WorkspaceSelector shows the empty state + Create action. Never navigate to '/'.
      if (selectedWorkspace) {
        setSelectedWorkspace(null);
      }
      // If we're still sitting on the old org's /w/:workspaceSlug route, leave it —
      // that stale slug is exactly what WorkspaceDashboard/BoardPage/etc. treat as
      // authoritative, and their slug-driven fetch would silently re-hydrate the old
      // org's workspace right back into the store (most reliably on next reload).
      // Route to the neutral in-app-shell redirector instead, same as a fresh login
      // with no workspace yet. Leave non-workspace pages (profile, admin, ...) alone.
      if (location.pathname.startsWith('/w/')) {
        navigate('/dashboard');
      }
      return;
    }

    const first = orgWorkspaces[0];
    setSelectedWorkspace(first);
    navigate(`/w/${first.workspaceSlug}`);
  }, [currentOrgId, allWorkspaces]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Switch org and its workspace together, in one tick.
   *
   * Doing this via the effect above leaves a window where currentOrgId is the
   * new org while selectedWorkspace still belongs to the old one. During that
   * window WorkspaceSelector's "workspace -> org" sync sees them disagree and
   * pushes the org back to the old workspace's org, so the switch appears to do
   * nothing (or worse, the sidebar empties). Resolving the workspace here means
   * the two values are never observed out of step, and the effect is left to
   * handle only the non-explicit cases: initial load, deep links, org creation.
   */
  const handleChange = (e: SelectChangeEvent) => {
    const nextOrgId = e.target.value;
    const orgWorkspaces = allWorkspaces.filter(w => w.organizationId === nextOrgId);

    // Claim it so the effect doesn't redo this switch.
    lastHandledOrgId.current = nextOrgId;
    setCurrentOrgId(nextOrgId);

    if (orgWorkspaces.length > 0) {
      const first = orgWorkspaces[0];
      setSelectedWorkspace(first);
      navigate(`/w/${first.workspaceSlug}`);
      return;
    }

    // No workspace in this org that the user can open. Clear the previous org's
    // workspace so its projects can't linger in the sidebar, and leave any stale
    // /w/:slug route for the neutral redirector.
    setSelectedWorkspace(null);
    if (location.pathname.startsWith('/w/')) {
      navigate('/dashboard');
    }
  };

  const handleCreate = async () => {
    if (!newOrgName.trim()) return;
    setIsCreating(true);
    try {
      const response = await api.post(ENDPOINTS.ORGANIZATIONS.CREATE, { name: newOrgName.trim() });
      const newOrg = response.data;
      addOrg(newOrg);
      setCurrentOrgId(newOrg.organizationId);
      setCreateDialogOpen(false);
      setNewOrgName('');
      toast.success('Organization created');
    } catch (error: any) {
      toast.error(error.message || 'Failed to create organization');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5}>
          ORGANIZATION
        </Typography>
        <AddIcon
          sx={{ fontSize: 16, color: 'text.secondary', cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          onClick={() => setCreateDialogOpen(true)}
        />
      </Box>

      {orgs.length > 0 ? (
        <Select
          value={currentOrgId ?? ''}
          onChange={handleChange}
          fullWidth
          size="small"
          sx={{ fontSize: '0.85rem', '.MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 1 } }}
        >
          {orgs.map(org => (
            <MenuItem key={org.organizationId} value={org.organizationId} sx={{ display: 'flex', gap: 1 }}>
              <Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem', bgcolor: 'primary.main' }}>
                {org.name.charAt(0)}
              </Avatar>
              <Typography variant="body2" noWrap sx={{ flex: 1 }}>{org.name}</Typography>
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Button size="small" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)} fullWidth variant="outlined">
          Create Organization
        </Button>
      )}

      {currentOrg && (
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
          <Chip label={`${currentOrg.workspaceCount} workspaces`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
          <Chip label={`${currentOrg.memberCount} members`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
        </Box>
      )}

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Organization</DialogTitle>
        <DialogContent>
          <TextField
            label="Organization Name"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            fullWidth autoFocus sx={{ mt: 1 }}
            placeholder="e.g. My Company, Engineering Team"
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!newOrgName.trim() || isCreating}>
            {isCreating ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrgSelector;
